import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in ms
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

export function useInactivityLogout() {
  const { user, signOut } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogout = useCallback(async () => {
    if (user) {
      await signOut();
      window.location.href = "/auth?reason=inactivity";
    }
  }, [user, signOut]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (user) {
      timerRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
    }
  }, [user, handleLogout]);

  useEffect(() => {
    if (!user) return;

    resetTimer();

    // Throttle high-frequency events (mousemove/scroll) via rAF + 1s gate
    // to avoid 'mousemove' handler performance violations.
    let rafScheduled = false;
    let lastReset = 0;
    const throttledReset = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        rafScheduled = false;
        const now = Date.now();
        if (now - lastReset < 1000) return;
        lastReset = now;
        resetTimer();
      });
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, throttledReset);
      });
    };
  }, [user, resetTimer]);
}
